import classNames from "classnames"
import React, { useRef } from "react"
import type { SwiperRef } from "swiper/react"
import { Swiper, SwiperSlide } from "swiper/react"
import type { SwiperOptions } from "swiper/types"

import { useReferrals } from "@bounce/referrals"
import { theme } from "@bounce/theme"

import { StoreSliderCard } from "./StoreSliderCard"
import { StoreSliderControls } from "./StoreSliderControls"
import type { PoiData } from "../../../libs/bounce/types"
import SafeLink from "../../Generic/SafeLink/SafeLink"
import { StoreItemListStructuredData } from "../../Generic/StructuredData/StoreItemListStructuredData"
import { useStoreSuggestions } from "../Store/hooks/useStoreSuggestions"
import { StoreSliderCardReview } from "@/components/LuggageStorage/StoreSlider/StoreSliderCardReview"
import type { CityStorageSpot, NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { useUrl } from "@/libs/router"
import type { PageType } from "@/types"
import { useAppURL } from "@/utils/appURL"

export type FeaturedPoiData = Pick<PoiData, "id" | "name" | "translatedName" | "coordinates">

export type StoreSliderProps = {
  className?: string
  cta?: string
  from: PageType
  lowestPrice: string
  name: string
  path: string
  searchQuery: string
  stores: NearbyStorageSpot[] | CityStorageSpot[]
  featuredPoi?: FeaturedPoiData
  renderStructuredData?: boolean
  swiperConfig?: SwiperOptions
  hideSliderNavigation?: boolean
}

type StoreSliderSwiperProps = Pick<
  StoreSliderProps,
  | "className"
  | "cta"
  | "from"
  | "searchQuery"
  | "stores"
  | "lowestPrice"
  | "path"
  | "name"
  | "swiperConfig"
  | "hideSliderNavigation"
  | "featuredPoi"
>

const DEFAULT_SWIPER_BREAKPOINTS = {
  [theme.spacing("screen-md")]: {
    slidesPerView: 1.5,
    slidesOffsetBefore: 18,
    slidesOffsetAfter: 16,
    spaceBetween: 32,
  },
  [theme.spacing("screen-lg")]: {
    slidesPerView: 2.15,
    slidesOffsetBefore: 18,
    slidesOffsetAfter: 16,
    spaceBetween: 32,
  },
  [theme.spacing("screen-xl")]: {
    slidesPerView: 2.6,
    slidesOffsetBefore: 24,
    slidesOffsetAfter: 16,
    spaceBetween: 32,
  },
}

const StoreSliderSwiper = ({
  className,
  cta,
  from,
  name,
  searchQuery,
  stores,
  swiperConfig,
  hideSliderNavigation = false,
  featuredPoi,
}: StoreSliderSwiperProps) => {
  const { getUrl } = useUrl()
  const buildAppURL = useAppURL()
  const swiperRef = useRef<SwiperRef>(null)
  const { coupon: couponCode } = useReferrals.useCodes()
  const { handleClickStore, handleGetStoreUrl } = useStoreSuggestions({ couponCode, from, searchQuery })

  const searchHref = buildAppURL(getUrl({ path: "/s" }), { query: searchQuery, coupon: couponCode })

  return (
    <>
      <div className="relative w-full">
        <div className="pointer-events-none absolute inset-y-0 -right-1 z-10 hidden w-10 bg-gradient-to-l from-white to-transparent md:block" />
        <Swiper
          breakpoints={DEFAULT_SWIPER_BREAKPOINTS}
          className={classNames("w-full", className)}
          ref={swiperRef}
          rewind
          slidesOffsetAfter={16}
          slidesOffsetBefore={24}
          slidesPerView={1.15}
          spaceBetween={16}
          tag="ul"
          watchSlidesProgress
          {...swiperConfig}>
          {stores.map(store => (
            <SwiperSlide key={store.id} tag="li">
              <SafeLink href={handleGetStoreUrl(store)} onClick={ev => handleClickStore(ev, store)} target="_self">
                <StoreSliderCard store={store} featuredPoi={featuredPoi} />
                {store.latestReview && <StoreSliderCardReview className="mt-5" review={store.latestReview} />}
              </SafeLink>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <StoreSliderControls
        cta={cta}
        searchHref={searchHref}
        name={name}
        from={from}
        hideSliderNavigation={hideSliderNavigation}
        swiperRef={swiperRef}
        storeCount={stores.length}
      />
    </>
  )
}

export const StoreSlider = (props: StoreSliderProps) => {
  const { className, renderStructuredData = true, path, stores } = props

  return (
    <>
      <StoreSliderSwiper className={classNames("mt-6", className)} {...props} />
      {renderStructuredData && <StoreItemListStructuredData path={path} stores={stores} />}
    </>
  )
}
